import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Printer, ArrowLeft, FileText, Columns2, Grid2X2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { getBillById, getSettings } from "@/lib/api";
import { amountToWords } from "@/lib/amountWords";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/09a62dc9-6a9e-4bad-b528-41b496ab2382/images/cbcacb73a9857c56eaa830e6342d9e2d9c033cf8fd7db1c24131862abae98730.png";

const f2 = (v) => (Number(v) || 0).toFixed(2);

// ── Full Invoice (1 per page) — exact current design ──
function FullInvoice({ bill, settings }) {
  return (
    <div className="bg-white p-6 font-mono text-sm">
      <div className="flex justify-between items-start border-b-2 border-black pb-3 mb-3">
        <div className="flex items-start gap-3">
          <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
          <div>
            <p className="text-base font-black font-heading tracking-tight">{settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
            <p className="text-[10px] text-zinc-600">{settings.address}</p>
            <p className="text-[10px] text-zinc-600">Ph: {settings.phone}</p>
          </div>
        </div>
        <div className="text-right border border-zinc-400 p-2 bg-zinc-50">
          <p className="text-[8px] font-bold uppercase text-zinc-500 mb-1">UPI</p>
          <QRCodeSVG value={`upi://pay?pa=${settings.upiId || "superflame@paytm"}&pn=${encodeURIComponent(settings.companyName || "Superflame Gas")}&am=${f2(bill.netPayable)}&tn=${encodeURIComponent(`SF ${bill.flatNo || bill.plotNo || ""} ${bill.invoiceNo} ${bill.customerName}`.trim())}&cu=INR`} size={60} level="M" className="mx-auto" />
          <p className="text-[8px] mt-1">{settings.upiId || "superflame@paytm"}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
        <p><span className="text-zinc-500">Invoice:</span> <b>{bill.invoiceNo}</b></p>
        <p className="text-right"><span className="text-zinc-500">Date:</span> <b>{new Date(bill.billDate).toLocaleDateString("en-IN")}</b></p>
        <p><span className="text-zinc-500">Due:</span> <b className="text-red-600">{new Date(bill.dueDate).toLocaleDateString("en-IN")}</b></p>
        <p className="text-right"><span className="text-zinc-500">Type:</span> <b>{bill.connectionType}</b></p>
      </div>
      <div className="border border-black p-2 mb-3 bg-zinc-50 text-xs">
        <div className="grid grid-cols-2 gap-1">
          <p><span className="text-zinc-500">Name:</span> <b>{bill.customerName}</b></p>
          <p><span className="text-zinc-500">No:</span> <b>{bill.consumerNumber}</b></p>
          <p><span className="text-zinc-500">Society:</span> {bill.society}</p>
          <p><span className="text-zinc-500">Flat:</span> {bill.flatNo || bill.plotNo}</p>
        </div>
      </div>
      <table className="w-full border-collapse border border-black mb-3 text-xs">
        <tbody>
          <tr><td className="border border-black p-1.5">Prev Reading</td><td className="border border-black p-1.5 text-right">{f2(bill.previousReading)}</td></tr>
          <tr><td className="border border-black p-1.5">Curr Reading</td><td className="border border-black p-1.5 text-right">{f2(bill.currentReading)}</td></tr>
          <tr><td className="border border-black p-1.5">Consumed</td><td className="border border-black p-1.5 text-right font-bold">{f2(bill.consumedUnits)}</td></tr>
          <tr><td className="border border-black p-1.5">KG/SCM</td><td className="border border-black p-1.5 text-right">{f2(bill.unitConsKgScm)}</td></tr>
        </tbody>
      </table>
      <table className="w-full border-collapse border border-black mb-3 text-xs">
        <tbody>
          <tr><td className="border border-black p-1.5">Usage ({f2(bill.unitConsKgScm)} KG x {"\u20B9"}{f2(bill.rate)})</td><td className="border border-black p-1.5 text-right">{"\u20B9"}{f2(bill.usageAmount)}</td></tr>
          <tr><td className="border border-black p-1.5">Service Charges</td><td className="border border-black p-1.5 text-right">{"\u20B9"}{f2(bill.serviceCharges)}</td></tr>
          <tr className="bg-orange-50 font-bold"><td className="border border-black p-1.5">NET PAYABLE</td><td className="border border-black p-1.5 text-right text-orange-700">{"\u20B9"}{f2(bill.netPayable)}</td></tr>
        </tbody>
      </table>
      <p className="text-[7px] italic text-zinc-500 mb-2 -mt-1">{amountToWords(bill.netPayable || 0)}</p>
      <div className="flex justify-between items-end text-[8px] border-t border-black pt-2">
        <p className="text-zinc-500">Computer generated. Ph: {settings.phone}</p>
        <div className="text-right"><p className="font-bold text-[10px]">FOR {settings.companyName}</p><p className="text-zinc-500 mt-2">Authorized Signatory</p></div>
      </div>
    </div>
  );
}

// ── Half Invoice (2 per page) — scaled-down SAME design ──
function HalfInvoice({ bill, settings }) {
  return (
    <div className="bg-white font-mono" style={{ fontSize: "9px", padding: "6px 10px" }}>
      {/* Header: Logo + Company + QR */}
      <div className="flex justify-between items-start border-b-2 border-black pb-1.5 mb-1.5">
        <div className="flex items-start gap-2">
          <img src={LOGO_URL} alt="" className="object-contain" style={{ height: "24px", width: "24px" }} />
          <div>
            <p className="font-black font-heading tracking-tight" style={{ fontSize: "11px" }}>{settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
            <p style={{ fontSize: "7px" }} className="text-zinc-600">{settings.address}</p>
            <p style={{ fontSize: "7px" }} className="text-zinc-600">Ph: {settings.phone}</p>
          </div>
        </div>
        <div className="text-right border border-zinc-400 bg-zinc-50" style={{ padding: "3px 5px" }}>
          <p style={{ fontSize: "6px" }} className="font-bold uppercase text-zinc-500 mb-0.5">UPI</p>
          <QRCodeSVG value={`upi://pay?pa=${settings.upiId || "superflame@paytm"}&pn=${encodeURIComponent(settings.companyName || "Superflame Gas")}&am=${f2(bill.netPayable)}&tn=${encodeURIComponent(`SF ${bill.flatNo || bill.plotNo || ""} ${bill.invoiceNo}`.trim())}&cu=INR`} size={36} level="M" className="mx-auto" />
          <p style={{ fontSize: "6px" }} className="mt-0.5">{settings.upiId || "superflame@paytm"}</p>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-4 gap-1 mb-1" style={{ fontSize: "8px" }}>
        <p><span className="text-zinc-500">Invoice:</span> <b>{bill.invoiceNo}</b></p>
        <p><span className="text-zinc-500">Date:</span> <b>{new Date(bill.billDate).toLocaleDateString("en-IN")}</b></p>
        <p><span className="text-zinc-500">Due:</span> <b className="text-red-600">{new Date(bill.dueDate).toLocaleDateString("en-IN")}</b></p>
        <p><span className="text-zinc-500">Type:</span> <b>{bill.connectionType}</b></p>
      </div>

      {/* Consumer Details */}
      <div className="border border-black bg-zinc-50 mb-1" style={{ padding: "3px 5px", fontSize: "8px" }}>
        <div className="grid grid-cols-2 gap-0.5">
          <p><span className="text-zinc-500">Name:</span> <b>{bill.customerName}</b></p>
          <p><span className="text-zinc-500">No:</span> <b>{bill.consumerNumber}</b></p>
          <p><span className="text-zinc-500">Society:</span> {bill.society}</p>
          <p><span className="text-zinc-500">Flat:</span> {bill.flatNo || bill.plotNo}</p>
        </div>
      </div>

      {/* Reading + Charges tables side by side */}
      <div className="grid grid-cols-2 gap-1 mb-1">
        <table className="w-full border-collapse border border-black" style={{ fontSize: "8px" }}>
          <tbody>
            <tr><td className="border border-black" style={{ padding: "1px 4px" }}>Prev Reading</td><td className="border border-black text-right" style={{ padding: "1px 4px" }}>{f2(bill.previousReading)}</td></tr>
            <tr><td className="border border-black" style={{ padding: "1px 4px" }}>Curr Reading</td><td className="border border-black text-right" style={{ padding: "1px 4px" }}>{f2(bill.currentReading)}</td></tr>
            <tr><td className="border border-black" style={{ padding: "1px 4px" }}>Consumed</td><td className="border border-black text-right font-bold" style={{ padding: "1px 4px" }}>{f2(bill.consumedUnits)}</td></tr>
            <tr><td className="border border-black" style={{ padding: "1px 4px" }}>KG/SCM</td><td className="border border-black text-right" style={{ padding: "1px 4px" }}>{f2(bill.unitConsKgScm)}</td></tr>
          </tbody>
        </table>
        <table className="w-full border-collapse border border-black" style={{ fontSize: "8px" }}>
          <tbody>
            <tr><td className="border border-black" style={{ padding: "1px 4px" }}>Rate</td><td className="border border-black text-right" style={{ padding: "1px 4px" }}>{"\u20B9"}{f2(bill.rate)}/KG</td></tr>
            <tr><td className="border border-black" style={{ padding: "1px 4px" }}>Usage</td><td className="border border-black text-right" style={{ padding: "1px 4px" }}>{"\u20B9"}{f2(bill.usageAmount)}</td></tr>
            <tr><td className="border border-black" style={{ padding: "1px 4px" }}>Service</td><td className="border border-black text-right" style={{ padding: "1px 4px" }}>{"\u20B9"}{f2(bill.serviceCharges)}</td></tr>
            <tr className="bg-orange-50 font-bold"><td className="border border-black" style={{ padding: "1px 4px" }}>NET PAYABLE</td><td className="border border-black text-right text-orange-700" style={{ padding: "1px 4px" }}>{"\u20B9"}{f2(bill.netPayable)}</td></tr>
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-end border-t border-black pt-1" style={{ fontSize: "7px" }}>
        <p className="text-zinc-500">Computer generated. Ph: {settings.phone}</p>
        <div className="text-right">
          <p className="font-bold" style={{ fontSize: "8px" }}>FOR {settings.companyName}</p>
          <p className="text-zinc-500 mt-0.5">Authorized Signatory</p>
        </div>
      </div>
    </div>
  );
}

// ── Compact Card (4 per page) — no QR, UPI text only ──
function CompactCard({ bill, settings }) {
  return (
    <div className="font-mono h-full flex flex-col justify-between bg-white" style={{ fontSize: "8px", padding: "5px" }}>
      <div>
        <p className="font-black font-heading tracking-tight text-center border-b border-black pb-0.5 mb-1" style={{ fontSize: "9px" }}>
          {settings.companyName || "SUPERFLAME GAS SERVICES"}
        </p>
        <div className="grid grid-cols-2 gap-0.5 mb-0.5">
          <p><span className="text-zinc-500">Inv:</span> <b>{bill.invoiceNo}</b></p>
          <p className="text-right"><span className="text-zinc-500">Date:</span> {new Date(bill.billDate).toLocaleDateString("en-IN")}</p>
        </div>
        <p className="font-bold" style={{ fontSize: "9px" }}>{bill.customerName}</p>
        <div className="grid grid-cols-2 gap-0.5 text-zinc-500">
          <p>No: {bill.consumerNumber}</p>
          <p className="text-right">{bill.society}</p>
        </div>
      </div>
      <div className="border-t border-black pt-1 mt-1 space-y-0.5">
        <div className="flex justify-between"><span className="text-zinc-500">Due:</span><span className="text-red-600 font-bold">{new Date(bill.dueDate).toLocaleDateString("en-IN")}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Units:</span><span className="font-bold">{f2(bill.consumedUnits)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Rate:</span><span>{"\u20B9"}{f2(bill.rate)}/KG</span></div>
        <div className="flex justify-between font-bold border-t border-black pt-0.5 mt-0.5" style={{ fontSize: "10px" }}>
          <span>Total:</span><span className="text-orange-700">{"\u20B9"}{f2(bill.netPayable)}</span>
        </div>
        <div className="flex justify-between">
          <span className={bill.isPaid ? "text-green-700 font-bold" : "text-red-600 font-bold"}>{bill.isPaid ? "PAID" : "UNPAID"}</span>
          <span className="text-zinc-500" style={{ fontSize: "7px" }}>UPI: {settings.upiId || "superflame@paytm"}</span>
        </div>
      </div>
    </div>
  );
}

function chunk(arr, size) {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
}

export default function BulkPrint() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [formatDialog, setFormatDialog] = useState(false);
  const [layout, setLayout] = useState(null);
  const printTriggered = useRef(false);

  useEffect(() => {
    const load = async () => {
      const ids = (searchParams.get("ids") || "").split(",").filter(Boolean);
      if (ids.length === 0) { setLoading(false); return; }
      const s = await getSettings();
      setSettings(s);
      const fetched = [];
      for (const id of ids) {
        try { const b = await getBillById(id); if (b) fetched.push(b); } catch (e) { console.warn(`Failed to fetch bill ${id}:`, e.message); }
      }
      setBills(fetched);
      setLoading(false);
    };
    load();
  }, [searchParams]);

  // Auto-navigate back after print dialog closes
  useEffect(() => {
    const onAfterPrint = () => {
      if (printTriggered.current) {
        printTriggered.current = false;
        navigate(-1);
      }
    };
    window.addEventListener("afterprint", onAfterPrint);
    return () => window.removeEventListener("afterprint", onAfterPrint);
  }, [navigate]);

  const handlePrint = (fmt) => {
    setLayout(fmt);
    setFormatDialog(false);
    printTriggered.current = true;
    setTimeout(() => window.print(), 400);
  };

  if (loading) return <div className="p-8 text-center text-zinc-400" data-testid="bulk-print-loading">Loading invoices...</div>;
  if (bills.length === 0) return (
    <div className="p-8 text-center text-zinc-400" data-testid="bulk-print-empty">
      <p>No invoices to print.</p>
      <Button onClick={() => navigate(-1)} variant="link" className="mt-4">Go Back</Button>
    </div>
  );

  return (
    <div data-testid="bulk-print-page">
      {/* Controls — hidden on print */}
      <div className="no-print p-4 md:p-8">
        <div className="flex flex-wrap items-center gap-4 mb-6 max-w-[800px] mx-auto">
          <Button data-testid="bulk-print-back" variant="outline" onClick={() => navigate(-1)} className="rounded-sm border-zinc-300">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button data-testid="bulk-print-button" onClick={() => setFormatDialog(true)} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
            <Printer className="w-4 h-4 mr-2" /> Print {bills.length} Invoice{bills.length !== 1 ? "s" : ""}
          </Button>
          <span className="text-sm text-zinc-500">{bills.length} invoice{bills.length !== 1 ? "s" : ""} ready</span>
        </div>
        <div className="max-w-[800px] mx-auto space-y-4">
          {bills.map((bill) => (
            <Card key={bill.id} className="border border-zinc-200 rounded-sm shadow-none">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">{bill.customerName}</p>
                  <p className="text-xs text-zinc-500 font-mono">{bill.invoiceNo} &middot; {new Date(bill.billDate).toLocaleDateString("en-IN")}</p>
                </div>
                <p className="font-mono font-bold text-orange-600">{"\u20B9"}{f2(bill.netPayable)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Format Selection Dialog */}
      <Dialog open={formatDialog} onOpenChange={setFormatDialog}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Print Format</DialogTitle>
            <DialogDescription>Choose layout for {bills.length} invoice{bills.length !== 1 ? "s" : ""}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-4">
            <button data-testid="format-1up" onClick={() => handlePrint("1up")} className="border-2 border-zinc-200 hover:border-orange-500 rounded-sm p-4 text-center transition-colors">
              <FileText className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
              <p className="text-sm font-bold">1 per page</p>
              <p className="text-[10px] text-zinc-500">Full invoice</p>
            </button>
            <button data-testid="format-2up" onClick={() => handlePrint("2up")} className="border-2 border-zinc-200 hover:border-orange-500 rounded-sm p-4 text-center transition-colors">
              <Columns2 className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
              <p className="text-sm font-bold">2 per page</p>
              <p className="text-[10px] text-zinc-500">Half-page each</p>
            </button>
            <button data-testid="format-4up" onClick={() => handlePrint("4up")} className="border-2 border-zinc-200 hover:border-orange-500 rounded-sm p-4 text-center transition-colors">
              <Grid2X2 className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
              <p className="text-sm font-bold">4 per page</p>
              <p className="text-[10px] text-zinc-500">Compact cards</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Print Content (visible only when printing) ── */}
      {layout && (
        <div className="print-root">
          {/* 1 per page */}
          {layout === "1up" && bills.map((bill, i) => (
            <div key={bill.id} className={i < bills.length - 1 ? "print-page-break" : ""}>
              <FullInvoice bill={bill} settings={settings} />
            </div>
          ))}

          {/* 2 per page — same design scaled down, scissors cut line */}
          {layout === "2up" && chunk(bills, 2).map((pair) => (
            <div key={`2up-${pair[0].id}`} className="print-2up">
              <div className="print-2up-item">
                <HalfInvoice bill={pair[0]} settings={settings} />
              </div>
              <div className="print-2up-divider" aria-hidden="true">
                {"- - - - - - - - - - - - - - - \u2702 - - - - - - - - - - - - - - -"}
              </div>
              {pair[1] ? (
                <div className="print-2up-item">
                  <HalfInvoice bill={pair[1]} settings={settings} />
                </div>
              ) : <div className="print-2up-item" />}
            </div>
          ))}

          {/* 4 per page — compact cards, dotted grid, no QR */}
          {layout === "4up" && chunk(bills, 4).map((group) => (
            <div key={`4up-${group[0].id}`} className="print-4up">
              {group.map((bill) => (
                <div key={bill.id} className="print-4up-item">
                  <CompactCard bill={bill} settings={settings} />
                </div>
              ))}
              {Array.from({ length: 4 - group.length }).map((_, j) => (
                <div key={`empty-${j}`} className="print-4up-item" />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
